'use client';

/**
 * ObeyaCostPlanReviewGroupsCard — the Concertina's body: `mcp_rev_diff`'s
 * `review_groups`, one expand/collapse section per real room type, lines
 * sorted by |$ impact| with a real impact bar per line.
 *
 * The direct answer to "if I change 104 products, how will these screen
 * layouts show it" (the e2e UX conversation that shaped this — see
 * docs/artifacts, klyde-workflow-poc.html, 'Save & Compare — notes'):
 * triage by room + magnitude, not an enumerated flat diff table. Every
 * grouping/sorting decision is server-side (queries/rev.py, rev_diff /
 * _resolve_line_room_type) — this card renders `review_groups` as-is,
 * same BFF discipline every other card here follows (see e.g.
 * mcp-watch-window-card.tsx's own docstring on the same lesson).
 *
 * Shares ONE `mcp_rev_diff` round-trip with the sibling header card
 * (obeya_costplan_review_header) via React Query dedup on
 * (strategy, params).
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — obeya_costplan_review_groups
 * @see klyde_edge/klyde_intelligence/projects/queries/rev.py — rev_diff / review_groups
 * @see klyde_ipe/components/canvas/cards/obeya/costplan-review-header-card.tsx — sibling header card
 *
 * @component_role: Card
 * @component_type: Overlay Body Card
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useState } from 'react';
import { ChevronRight, Minus, Pencil } from 'lucide-react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useMcpRevDiff, type McpRevDiffReviewLine } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCostPlanCompareStore } from '@/lib/stores/costplan-compare-store';

function money(v: number): string {
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

// Plain (unsigned) formatter for a from/to VALUE — qty_from/price_to are
// absolute quantities/prices, not deltas, so they never take the +/− sign
// `money()` puts on an impact figure.
function num(v: number): string {
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// The row's one change-note line — real before/after for the numeric
// fields that actually moved ("125 → 132"), falling back to
// field_note's short description for a non-numeric swap (product/
// vendor). A removed line has neither: its own Removed pill already
// says what happened, so this renders nothing rather than repeating it.
function changeNote(line: McpRevDiffReviewLine): string {
  const parts: string[] = [];
  if (line.qty_from != null && line.qty_to != null) parts.push(`qty ${num(line.qty_from)} → ${num(line.qty_to)}`);
  if (line.price_from != null && line.price_to != null) parts.push(`price ${num(line.price_from)} → ${num(line.price_to)}`);
  if (line.field_note) parts.push(line.field_note);
  return parts.join(', ');
}

function ImpactBar({ impact, maxAbs }: { impact: number; maxAbs: number }) {
  const pct = maxAbs > 0 ? Math.min(100, (Math.abs(impact) / maxAbs) * 100) : 0;
  return (
    <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
      <div
        className={cn('h-full rounded-full', impact >= 0 ? 'bg-amber-500' : 'bg-emerald-500')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ReviewLineRow({ line, maxAbs, L }: { line: McpRevDiffReviewLine; maxAbs: number; L: Record<string, string> }) {
  const pill = line.kind === 'removed'
    ? { label: L.pill_removed ?? '', cls: 'bg-destructive/10 text-destructive' }
    : { label: L.pill_changed ?? '', cls: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' };
  return (
    <div className="flex items-center gap-3 px-3 py-1.5">
      <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0', pill.cls)}>
        {line.kind === 'removed' ? <Minus className="w-2.5 h-2.5" /> : <Pencil className="w-2.5 h-2.5" />}
        {pill.label}
      </span>
      <div className="flex-1 min-w-0">
        <div className={cn(text.title, 'truncate')}>{line.product_name || '—'}</div>
        <div className={cn(text.muted, 'truncate')}>
          {line.vendor_name || '—'}{changeNote(line) ? ` · ${changeNote(line)}` : ''}
        </div>
        <div className="mt-1">
          <ImpactBar impact={line.impact} maxAbs={maxAbs} />
        </div>
      </div>
      <div className={cn(
        text.value, 'shrink-0 tabular-nums text-right whitespace-nowrap',
        line.impact >= 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400',
      )}>{money(line.impact)}</div>
    </div>
  );
}

export function ObeyaCostPlanReviewGroupsCard() {
  const { mcpId } = useViewportSelection();
  const { contentKey } = useCardIdentity();
  const lang = useLanguageStore((s) => s.lang);

  const compareRevId = useCostPlanCompareStore((s) => s.compareRevId);
  const compareMcpId = useCostPlanCompareStore((s) => s.compareMcpId);
  const compareAgainstRevId = useCostPlanCompareStore((s) => s.compareAgainstRevId);
  const effectiveRevId = compareMcpId && mcpId && compareMcpId === mcpId ? compareRevId : null;
  const effectiveAgainstRevId = effectiveRevId ? compareAgainstRevId : null;

  const diffStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'rev_diff'));
  const { data: raw, isLoading } = useMcpRevDiff(diffStrategy, mcpId ?? null, effectiveRevId, effectiveAgainstRevId, lang ?? undefined);
  const diff = raw?.data;
  const L = diff?.labels ?? {};
  const groups = diff?.review_groups ?? [];

  // Every group starts expanded — the whole point of grouping by room is
  // to make everything scannable at once via triage (impact bars, sort),
  // not to hide content behind another click. Collapse is an escape
  // hatch for a room the user's already reviewed, not the default.
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (roomType: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(roomType)) next.delete(roomType); else next.add(roomType);
      return next;
    });

  const maxAbs = useMemo(
    () => groups.reduce((m, g) => Math.max(m, ...g.lines.map((l) => Math.abs(l.impact))), 0),
    [groups],
  );

  const queryKey = ['strategy-data', diffStrategy, { semantic_id: mcpId ?? '', rev_sid: effectiveRevId ?? '', ...(effectiveAgainstRevId ? { compare_rev_sid: effectiveAgainstRevId } : {}), ...(lang ? { lang } : {}) }] as const;
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_review_groups',
    surface: 'overlay' as const,
    status: (effectiveRevId ? (diff ? 'loaded' : 'loading') : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: effectiveRevId && diffStrategy ? queryKey : null,
    text: groups.length > 0 ? `${groups.length} room${groups.length === 1 ? '' : 's'} affected` : (L.review_no_changes ?? ''),
    json: diff ? { review_groups: diff.review_groups } : null,
  }), [contentKey, effectiveRevId, diff, diffStrategy, queryKey, groups.length, L.review_no_changes]));

  if (!effectiveRevId) return null;

  if (isLoading) {
    return <div className={cn(text.muted, 'px-3 py-3')}>…</div>;
  }

  if (groups.length === 0) {
    return <div className={cn(text.muted, 'px-3 py-3')}>{L.review_no_changes ?? ''}</div>;
  }

  return (
    <div className="flex flex-col divide-y divide-border">
      {groups.map((group) => {
        const isOpen = !collapsed.has(group.room_type);
        const netImpact = group.lines.reduce((sum, l) => sum + l.impact, 0);
        return (
          <div key={group.room_type}>
            <button
              type="button"
              onClick={() => toggle(group.room_type)}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50"
            >
              <ChevronRight className={cn('w-3.5 h-3.5 shrink-0 transition-transform text-muted-foreground', isOpen && 'rotate-90')} />
              <span className={cn(text.title, 'flex-1 min-w-0 text-left truncate')}>{group.room_label}</span>
              <span className={cn(text.muted, 'shrink-0')}>{group.lines.length}</span>
              <span className={cn(text.value, 'shrink-0 tabular-nums text-right whitespace-nowrap')}>{money(netImpact)}</span>
            </button>
            {isOpen && (
              <div className="divide-y divide-border/60 pb-1">
                {group.lines.map((line) => (
                  <ReviewLineRow key={line.line_id} line={line} maxAbs={maxAbs} L={L} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
